import React, { useState, useEffect } from 'react';
import api from '../api';
import { norm } from '../utils';
import { imprimirRecibo } from '../platform/arquivos';

const noScrollNumber = {
  onWheel: (e) => e.currentTarget.blur(),
  onKeyDown: (e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); },
};

function gramasDesejadas(qtd, unidade, ficha) {
  const n = Number(qtd) || 0;
  if (n <= 0) return 0;
  if (unidade === 'kg') return n * 1000;
  if (unidade === 'porcoes') {
    const peso = Number(ficha?.peso_total) || 0;
    const porcoes = Number(ficha?.porcoes) || 0;
    if (peso > 0 && porcoes > 0) return n * (peso / porcoes);
    return 0;
  }
  if (unidade === 'receitas') {
    const peso = Number(ficha?.peso_total) || 0;
    if (peso > 0) return n * peso;
    return 0;
  }
  return n;
}

function imprimirTermica({ nome, nReceitas, insumos, embalagens, rendimentoTotal = 0, porcoesTotal = 0, unidadeRendimento = '', modoPreparo = '' }) {
  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const fmt = (n, unid) => {
    const v = Number(n) || 0;
    const s = v % 1 === 0 ? v.toLocaleString('pt-BR') : v.toFixed(2).replace('.', ',');
    return `${s} ${esc(unid || '')}`.trim();
  };
  const itemRow = (nome, total, unid) => `
    <div class="item">
      <div class="check"></div>
      <div class="name">${esc(nome)}</div>
      <div class="qty">${fmt(total, unid)}</div>
    </div>`;

  const insumosHtml = insumos.map(i => itemRow(i.nome, (Number(i.quantidade) || 0) * nReceitas, i.unidade_medida)).join('');
  const embHtml = embalagens.length ? `
    <div class="section">Embalagens</div>
    ${embalagens.map(e => itemRow(e.nome, Math.ceil((Number(e.quantidade) || 0) * nReceitas), e.unidade_medida || 'un')).join('')}
  ` : '';
  const modoHtml = (modoPreparo && String(modoPreparo).trim()) ? `
    <div class="section">Modo de Preparo (${nReceitas}x)</div>
    <div class="modo">${esc(modoPreparo).replace(/\n/g, '<br>')}</div>
  ` : '';

  const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Preparo | ${esc(nome)} ${nReceitas}x</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { width: 80mm; background: #fff; }
  body { font-family: Arial, Helvetica, sans-serif; color:#000; padding:3mm 3mm;
         font-size:15px; line-height:1.3; -webkit-font-smoothing:none; }
  .header { text-align:center; }
  .header h1 { font-size:22px; font-weight:900; letter-spacing:1px; }
  .subtitle { text-align:center; font-size:17px; font-weight:700; margin-top:1px; }
  .batch { text-align:center; font-size:18px; font-weight:900; margin:5px 0 6px;
           border:2px solid #000; padding:3px 0; }
  .rule { border:none; border-top:1px dashed #000; margin:5px 0; }
  .rule-solid { border:none; border-top:3px solid #000; margin:4px 0 8px; }
  .section { font-size:14px; font-weight:900; text-transform:uppercase; text-align:center;
             margin:9px 0 3px; padding:3px 0; border-top:2px solid #000; border-bottom:2px solid #000; }
  .item { display:flex; align-items:center; padding:7px 0; border-bottom:1px dotted #555; }
  .item:last-child { border-bottom:none; }
  .check { flex:0 0 auto; width:16px; height:16px; border:2px solid #000; margin-right:8px; }
  .name { flex:1 1 auto; font-size:15px; font-weight:700; text-transform:uppercase; line-height:1.2; padding-right:6px; }
  .qty { flex:0 0 auto; font-size:18px; font-weight:900; white-space:nowrap; text-align:right; }
  .yield { text-align:center; font-size:14px; font-weight:700; margin:6px 0 8px;
           padding:5px 0; background:#000; color:#fff; }
  .yield small { display:block; font-size:11px; font-weight:600; margin-top:2px; opacity:.85; }
  .modo { font-size:14px; line-height:1.5; padding:6px 2px 4px; text-align:left;
          white-space:normal; word-wrap:break-word; }
  .footer { text-align:center; font-size:13px; margin-top:10px; }
  .footer .date { font-size:13px; margin-top:4px; }
  .print-bar { text-align:center; margin-bottom:12px; }
  .print-bar button { font-size:14px; padding:8px 18px; cursor:pointer; }
  @media print { .print-bar { display:none; } body { padding:2mm 2mm; } }
</style></head><body>
<div class="print-bar"><button onclick="window.print()">🖨️ Imprimir</button></div>
<div class="header"><h1>PREPARO</h1></div>
<div class="subtitle">${esc(nome)}</div>
<div class="batch">RECEITA ${nReceitas}x</div>
${rendimentoTotal > 0 ? `<div class="yield">RENDIMENTO: ${Math.round(rendimentoTotal).toLocaleString('pt-BR')}g${porcoesTotal > 0 ? `<small>${porcoesTotal.toLocaleString('pt-BR')} ${esc(unidadeRendimento || 'porções')}</small>` : ''}</div>` : ''}
<hr class="rule-solid">
${insumosHtml}
${embHtml}
${modoHtml}
<hr class="rule">
<div class="footer">Quantidade de Receitas: <strong>${nReceitas}x</strong><div class="date">_____ / _____ / _____</div></div>
</body></html>`;

  const slug = String(nome || 'preparo').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'preparo';

  // Mesmo conteudo do HTML acima, em blocos — e o que o app desenha no PDF.
  // Mexeu num, olhe o outro (o porque esta em platform/arquivos.js).
  const linhaItem = (it) => ({
    t: 'par',
    esq: it.nome,
    dir: fmt((Number(it.quantidade) || 0) * (nReceitas || 1), it.unidade_medida || ''),
  });
  const blocos = [
    { t: 'titulo', texto: nome },
    { t: 'linha', texto: `${nReceitas}x receita${nReceitas !== 1 ? 's' : ''}`, fraco: true },
    rendimentoTotal > 0 && { t: 'linha', fraco: true,
      texto: `Rendimento: ${rendimentoTotal.toLocaleString('pt-BR')}${unidadeRendimento || 'g'}`
        + (porcoesTotal > 0 ? ` · ${porcoesTotal} porções` : '') },
    { t: 'divisoria' },
    { t: 'linha', texto: 'Ingredientes', forte: true },
    ...insumos.map(linhaItem),
    ...(embalagens.length ? [{ t: 'divisoria' }, { t: 'linha', texto: 'Embalagens', forte: true }] : []),
    ...embalagens.map(linhaItem),
    ...(modoPreparo && String(modoPreparo).trim()
      ? [{ t: 'divisoria' }, { t: 'linha', texto: 'Modo de preparo', forte: true },
         { t: 'linha', texto: String(modoPreparo) }]
      : []),
    { t: 'espaco' },
    { t: 'linha', texto: '_____ / _____ / _____', fraco: true, alinhar: 'centro' },
  ];

  return imprimirRecibo({ html, blocos, nome: `${slug}.pdf`, larguraMm: 80 });
}

export default function CalculadoraProducao() {
  const [fichas, setFichas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [fichaSel, setFichaSel] = useState(null);
  const [fichaDet, setFichaDet] = useState(null);
  const [carregandoFicha, setCarregandoFicha] = useState(false);
  const [unidade, setUnidade] = useState('gramas');
  const [qtd, setQtd] = useState('');
  const [erro, setErro] = useState('');

  useEffect(() => { carregarFichas(); }, []);

  async function carregarFichas() {
    setLoading(true);
    try {
      const res = await api.get('/fichas/api/fichas-tecnicas');
      const lista = res.data?.fichas || [];
      setFichas(lista.filter(f => f.ativo !== false));
    } catch {
      setErro('Erro ao carregar fichas técnicas');
    } finally { setLoading(false); }
  }

  async function selecionarFicha(f) {
    setFichaSel(f);
    setFichaDet(null);
    setCarregandoFicha(true);
    setErro('');
    try {
      const res = await api.get(`/fichas/api/fichas-tecnicas/${f.id}`);
      if (res.data.success) {
        setFichaDet(res.data.ficha);
        // Define unidade default conforme rendimento
        const rend = (res.data.ficha.unidade_rendimento || '').toLowerCase();
        if (rend.includes('porç') || rend.includes('porc')) setUnidade('porcoes');
        else setUnidade('gramas');
      }
    } catch {
      setErro('Erro ao carregar ficha técnica');
    } finally { setCarregandoFicha(false); }
  }

  const fichasFiltradas = busca
    ? fichas.filter(f => norm(f.nome).includes(norm(busca)) || norm(f.categoria || '').includes(norm(busca)))
    : fichas;

  const temPorcoes = fichaDet && Number(fichaDet.peso_total) > 0 && Number(fichaDet.porcoes) > 0;
  const gramas = fichaDet ? gramasDesejadas(qtd, unidade, fichaDet) : 0;
  const pesoReceita = Number(fichaDet?.peso_total) || 0;
  const nReceitas = pesoReceita > 0 && gramas > 0 ? Math.ceil(gramas / pesoReceita) : null;
  const sobra = nReceitas != null ? Math.round((nReceitas * pesoReceita - gramas) * 10) / 10 : null;

  const insumos = (fichaDet?.itens || []).filter(i => i.tipo === 'produto' || i.tipo === 'insumo' || !i.tipo);
  const embalagens = (fichaDet?.itens || []).filter(i => i.tipo === 'embalagem');
  const subReceitas = fichaDet?.sub_receitas || [];

  function formatRendimento(f) {
    const peso = Number(f.peso_total) || 0;
    const porc = Number(f.porcoes) || 0;
    const u = f.unidade_rendimento || '';
    if (porc > 0 && peso > 0) return `${porc} porções de ${(peso / porc).toFixed(0)}g`;
    if (peso > 0) return `${peso.toLocaleString('pt-BR')}${u || 'g'}`;
    return '—';
  }

  if (loading) return <div className="p-6 text-center text-gray-400">Carregando fichas técnicas...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-3 sm:mb-6">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-800">Calculadora de Produção</h1>
        <p className="hidden sm:block text-gray-500 text-sm mt-1">Escale qualquer ficha técnica para gramas, kg ou porções</p>
      </div>

      {erro && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm">{erro}</div>}

      <div className="space-y-4">
        {/* Passo 1 — Seleção da ficha (some quando há ficha) */}
        {!fichaSel && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-3 sm:px-5 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider bg-pink-50 text-pink-700 rounded-full px-2 py-0.5">Passo 1</span>
              <span className="font-semibold text-gray-800 text-sm">Escolha uma ficha técnica</span>
            </div>
            <div className="p-4">
              <input
                type="text"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Buscar por nome ou categoria..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 mb-3" />
              {fichasFiltradas.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-400">
                  <div className="text-3xl mb-2">🔍</div>
                  Nenhuma ficha encontrada para "{busca}".
                </div>
              ) : (
                /* Lista, nao grade de cartoes: cada ficha ocupava um bloco de tres
                   linhas com faixa colorida no topo, e uma confeitaria com 40 fichas
                   virava uma rolagem interminavel so para escolher uma. Em linha,
                   cabem oito por tela e o nome continua inteiro. */
                <div className="max-h-[26rem] overflow-y-auto -mx-1 px-1 divide-y divide-gray-100 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-2">
                  {fichasFiltradas.map(f => (
                    <button key={f.id} type="button" onClick={() => selecionarFicha(f)}
                      className="w-full text-left flex items-center gap-3 py-2.5 px-1 sm:px-3 sm:rounded-xl sm:border sm:border-gray-200 hover:bg-pink-50/60 sm:hover:border-pink-300 transition-colors">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-800 text-sm truncate">{f.nome}</div>
                        <div className="text-xs text-gray-400 truncate">
                          {f.categoria ? f.categoria + ' · ' : ''}{formatRendimento(f)}
                        </div>
                      </div>
                      <span className="text-gray-300 shrink-0">›</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Passo 2 — Configuração */}
        {fichaSel && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="hidden sm:block h-1 bg-gradient-to-r from-pink-400 to-amber-300" />
            <div className="px-3 sm:px-5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider bg-pink-50 text-pink-700 rounded-full px-2 py-0.5">Passo 2</span>
                  <span className="font-semibold text-gray-800 text-sm truncate">{fichaSel.nome}</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{formatRendimento(fichaSel)}</p>
              </div>
              <button type="button" onClick={() => { setFichaSel(null); setFichaDet(null); setQtd(''); }}
                className="text-xs text-gray-500 hover:text-pink-600 border border-gray-200 hover:border-pink-300 px-2.5 py-1.5 rounded-lg shrink-0 transition-colors">
                ← Trocar ficha
              </button>
            </div>
            <div className="p-3 sm:p-5">
              {carregandoFicha ? (
                <p className="text-center text-gray-400 py-4 text-sm">Carregando ficha...</p>
              ) : (
                <>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Unidade</label>
                  {/* Uma coluna por opção. Com flex-wrap, "receitas" caia sozinha numa
                      segunda linha e o seletor virava um bloco torto de duas alturas.
                      Colunas iguais cabem as quatro na largura do celular. */}
                  {(() => {
                    const opcoes = [
                      { v: 'gramas', label: 'gramas' },
                      { v: 'kg', label: 'kg' },
                      ...(temPorcoes ? [{ v: 'porcoes', label: 'porções' }] : []),
                      ...(pesoReceita > 0 ? [{ v: 'receitas', label: 'receitas' }] : []),
                    ];
                    return (
                      <div className="grid border border-gray-200 rounded-xl overflow-hidden mb-3.5"
                        style={{ gridTemplateColumns: `repeat(${opcoes.length}, minmax(0, 1fr))` }}>
                        {opcoes.map(u => (
                          <button key={u.v} type="button" onClick={() => setUnidade(u.v)}
                            className={`px-1 py-2 text-[13px] sm:text-sm font-semibold truncate transition-colors ${
                              unidade === u.v ? 'bg-pink-500 text-white' : 'bg-white text-gray-500 hover:bg-pink-50'
                            }`}>
                            {u.label}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Quantidade</label>
                  {/* Controle de toque, nao so campo numerico: quem usa esta em pe na
                      cozinha, com a mao suja. Os botoes tem 56px de lado — alvo confortavel
                      sem mirar. O campo segue editavel para quem quer um valor exato. */}
                  <div className="flex items-stretch gap-2">
                    <button type="button" aria-label="Diminuir"
                      onClick={() => { const passo = unidade === 'kg' ? 0.5 : unidade === 'gramas' ? 100 : 1;
                                       setQtd(String(Math.max(0, (Number(qtd) || 0) - passo))); }}
                      className="w-12 h-12 shrink-0 rounded-xl border border-gray-200 bg-white text-xl font-bold text-gray-500 active:bg-gray-100">
                      −
                    </button>
                    <input type="number" min="0" inputMode="decimal"
                      step={unidade === 'kg' ? '0.1' : '1'}
                      value={qtd}
                      onChange={e => setQtd(e.target.value)}
                      {...noScrollNumber}
                      placeholder={unidade === 'kg' ? '1.5' : (unidade === 'porcoes' ? '8' : unidade === 'receitas' ? '8' : '1500')}
                      className="flex-1 min-w-0 h-12 text-center border border-gray-200 rounded-xl px-2 text-xl font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-pink-200" />
                    <button type="button" aria-label="Aumentar"
                      onClick={() => { const passo = unidade === 'kg' ? 0.5 : unidade === 'gramas' ? 100 : 1;
                                       setQtd(String((Number(qtd) || 0) + passo)); }}
                      className="w-12 h-12 shrink-0 rounded-xl border border-gray-200 bg-white text-xl font-bold text-gray-500 active:bg-gray-100">
                      +
                    </button>
                    <span className="self-center text-sm font-semibold text-gray-500 pl-1 shrink-0">{unidade === 'kg' ? 'kg' : unidade === 'porcoes' ? 'porções' : unidade === 'receitas' ? 'rec.' : 'g'}</span>
                  </div>
                  {gramas > 0 && pesoReceita > 0 && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-green-600 mb-0.5">Você vai produzir</p>
                      <p className="text-sm text-gray-800">
                        <span className="font-bold">{gramas.toLocaleString('pt-BR')}g</span>
                        <span className="text-gray-500"> · </span>
                        <span className="font-semibold text-green-700">{nReceitas} receita{nReceitas !== 1 ? 's' : ''}</span>
                        {sobra != null && sobra > 0 && (
                          <span className="text-xs text-gray-500"> + {sobra.toLocaleString('pt-BR')}g de sobra</span>
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Passo 3 — Resultado */}
        {fichaDet && gramas > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="hidden sm:block h-1 bg-green-400" />
            <div className="px-3 sm:px-5 py-2.5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 rounded-full px-2 py-0.5">Resultado</span>
                <span className="font-semibold text-gray-800 text-sm">Quantidades para preparar</span>
              </div>
              {/* No app isto gera um PDF e abre a folha de compartilhamento — de
                  onde da para imprimir (AirPrint), salvar ou mandar no WhatsApp.
                  Antes o botao ficava escondido no app porque window.open devolve
                  null ali e tocar nele nao fazia nada. */}
              {<button onClick={() => imprimirTermica({
                nome: fichaSel?.nome || fichaDet?.nome || 'Preparo',
                nReceitas: nReceitas || 1,
                insumos,
                embalagens,
                rendimentoTotal: nReceitas ? nReceitas * pesoReceita : 0,
                porcoesTotal: (nReceitas && fichaDet?.porcoes) ? nReceitas * Number(fichaDet.porcoes) : 0,
                unidadeRendimento: fichaDet?.unidade_rendimento || '',
                modoPreparo: fichaDet?.modo_preparo || '',
              })}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap">
                <span className="sm:hidden">📄 PDF</span><span className="hidden sm:inline">🖨️ Imprimir</span>
              </button>}
            </div>
            <div className="p-3 sm:p-5 space-y-3">
              {pesoReceita <= 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl p-4 text-sm">
                  ⚠️ Cadastre o peso total da ficha técnica para calcular receitas e ingredientes.
                </div>
              ) : (
                <>
                  {/* Faixa unica em vez de tres cartoes. Os numeros sao curtos e se
                      leem juntos ("12 receitas rendem 15.600 g, sobram 800 g"); em
                      cartoes de 100px de altura cada, ocupavam meia tela para dizer
                      isso e empurravam os ingredientes — que sao o motivo da tela —
                      para baixo da dobra. */}
                  <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 sm:divide-y-0 sm:grid sm:grid-cols-3 sm:divide-x">
                    <div className="flex items-baseline justify-between gap-2 px-3 py-2 sm:block">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Receitas</span>
                      <span className="sm:block sm:mt-0.5">
                        <span className="text-lg font-bold text-gray-800">{nReceitas}×</span>
                        <span className="text-xs text-gray-400 ml-1.5">{Number(pesoReceita).toLocaleString('pt-BR')}g cada</span>
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2 px-3 py-2 sm:block">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-pink-600">Rendimento</span>
                      <span className="sm:block sm:mt-0.5">
                        <span className="text-lg font-bold text-pink-700">{(nReceitas * pesoReceita).toLocaleString('pt-BR')}g</span>
                        {fichaDet?.porcoes > 0 && (
                          <span className="text-xs text-pink-600/70 ml-1.5">
                            {(nReceitas * Number(fichaDet.porcoes)).toLocaleString('pt-BR')} {fichaDet.unidade_rendimento || 'porções'}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2 px-3 py-2 sm:block">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${sobra > 0 ? 'text-amber-600' : 'text-green-600'}`}>Sobra</span>
                      <span className="sm:block sm:mt-0.5">
                        <span className={`text-lg font-bold ${sobra > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                          {sobra != null ? sobra.toLocaleString('pt-BR') : '0'}g
                        </span>
                        <span className={`text-xs ml-1.5 ${sobra > 0 ? 'text-amber-600/70' : 'text-green-600/70'}`}>
                          {sobra > 0 ? 'após produção' : 'sem sobra'}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Ingredientes */}
                  {insumos.length > 0 && (
                    <div className="overflow-x-auto">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">🧂 Ingredientes</h4>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                            <th className="text-left px-3 py-2 rounded-l-lg">Ingrediente</th>
                            <th className="text-right px-3 py-2 hidden sm:table-cell">Qtd por receita</th>
                            <th className="text-right px-3 py-2 rounded-r-lg">Total ({nReceitas}×)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {insumos.map((it, j) => {
                            const q = Number(it.quantidade) || 0;
                            const total = q * nReceitas;
                            const u = it.unidade_medida || '';
                            return (
                              <tr key={j} className="border-b border-gray-50">
                                <td className="px-3 py-2 text-gray-700">{it.nome}</td>
                                <td className="px-3 py-2 text-right text-gray-500 hidden sm:table-cell">{q.toLocaleString('pt-BR')} {u}</td>
                                <td className="px-3 py-2 text-right font-semibold text-gray-800">{total.toLocaleString('pt-BR')} {u}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Sub-receitas */}
                  {subReceitas.length > 0 && (
                    <div className="overflow-x-auto">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">📋 Sub-receitas</h4>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-pink-50 text-xs text-pink-600 uppercase tracking-wider">
                            <th className="text-left px-3 py-2 rounded-l-lg">Sub-receita</th>
                            <th className="text-right px-3 py-2 hidden sm:table-cell">Qtd por receita</th>
                            <th className="text-right px-3 py-2 rounded-r-lg">Total ({nReceitas}×)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subReceitas.map((s, j) => {
                            const q = Number(s.quantidade) || 0;
                            const total = q * nReceitas;
                            const u = s.unidade_medida || '';
                            return (
                              <tr key={j} className="border-b border-gray-50">
                                <td className="px-3 py-2 text-gray-700">{s.nome || s.ficha_filha_nome || `Ficha #${s.ficha_tecnica_filha_id}`}</td>
                                <td className="px-3 py-2 text-right text-gray-500 hidden sm:table-cell">{q.toLocaleString('pt-BR')} {u}</td>
                                <td className="px-3 py-2 text-right font-semibold text-gray-800">{total.toLocaleString('pt-BR')} {u}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <p className="text-xs text-gray-400 mt-2 italic">Para detalhar os ingredientes de cada sub-receita, use a Calculadora também nelas.</p>
                    </div>
                  )}

                  {/* Modo de preparo */}
                  {fichaDet.modo_preparo && (
                    <details className="border border-gray-100 rounded-xl">
                      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-gray-700 select-none">
                        👨‍🍳 Modo de Preparo (×{nReceitas})
                      </summary>
                      <div className="px-4 pb-4 text-sm text-gray-600 whitespace-pre-wrap font-mono leading-relaxed">
                        {fichaDet.modo_preparo}
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
